/**
 * Global error boundary — catches React render crashes and shows a recovery UI
 * instead of a white screen.
 *
 * STYLING EXCEPTION — this is the one surface that may NOT use `getClassMap()`.
 * It sits in main.tsx's EAGER import graph (App.tsx wraps the whole tree in it),
 * and the ClassMap bonds are wired lazily by `lazyPage` / `useUiProviders`. A
 * `cm.button(...)` call here would throw "No UIClassMap has been set" from inside
 * the boundary's own render — replacing the recovery UI with the white screen it
 * exists to prevent — and would trip `__tests__/eager-classmap-gate.test.ts`.
 * So its buttons stay hand-styled, and in exchange they carry theme tokens (no
 * raw hex), `data-mol-id`s, i18n text, and the 36px compact touch floor by hand.
 */

import { Component, type ErrorInfo, type ReactNode } from 'react'

import { t } from '@molecule/app-i18n'

import { reportClientError } from '../utilities/error-reporting.js'

interface Props {
  children: ReactNode
}

interface State {
  hasError: boolean
  error: Error | null
  showDetail: boolean
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, error: null, showDetail: false }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error }
  }

  /**
   * OBS1: a React render crash used to surface this recovery UI but report
   * NOWHERE. Forward it (with the component stack) to the first-party analytics
   * endpoint so it becomes a queryable, subsystem-tagged `error` event the admin
   * can aggregate. Best-effort — `reportClientError` never throws.
   */
  componentDidCatch(error: Error, info: ErrorInfo): void {
    reportClientError({
      source: 'react',
      error,
      ...(info.componentStack ? { componentStack: info.componentStack } : {}),
    })
  }

  render(): ReactNode {
    if (!this.state.hasError) return this.props.children

    return (
      <div
        style={{
          height: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 16,
          padding: 24,
          background: 'var(--mol-color-background, #0e0e0e)',
          color: 'var(--mol-color-text, #e0e0e0)',
          fontFamily: 'Arimo, system-ui, sans-serif',
        }}
      >
        <div style={{ fontSize: 20, fontWeight: 600 }}>
          {t('errorBoundary.title', undefined, { defaultValue: 'Something went wrong' })}
        </div>
        <div
          style={{
            fontSize: 14,
            color: 'var(--mol-color-text-secondary, #aaaaaa)',
            maxWidth: 400,
            textAlign: 'center',
            lineHeight: 1.6,
          }}
        >
          {t('errorBoundary.message', undefined, {
            defaultValue: 'An unexpected error occurred. Try reloading the page.',
          })}
        </div>

        <div style={{ display: 'flex', gap: 12, marginTop: 8 }}>
          {/* mol-bespoke-button: the global boundary may not call getClassMap()
              (see the module header) — it is eagerly imported and the ClassMap
              bonds are wired lazily, so cm.button() here would throw inside the
              very render that prevents a white screen. Primary recovery action,
              hand-matched to cm.button({ color: 'primary' }), tokens only. */}
          <button
            type="button"
            data-mol-id="error-boundary-reload"
            onClick={() => window.location.reload()}
            style={{
              fontSize: 14,
              minHeight: 36,
              padding: '8px 20px',
              borderRadius: 8,
              border: '1px solid transparent',
              background: 'var(--mol-color-primary, #6d5dfc)',
              color: 'var(--color-on-primary, #fff)',
              cursor: 'pointer',
            }}
          >
            {t('errorBoundary.reload', undefined, { defaultValue: 'Reload' })}
          </button>
          {/* mol-bespoke-button: same eager-graph ClassMap exception as above.
              Secondary sibling, hand-matched to cm.button({ variant: 'outline' }). */}
          <button
            type="button"
            data-mol-id="error-boundary-go-home"
            onClick={() => {
              this.setState({ hasError: false, error: null })
              window.location.hash = ''
              window.location.pathname = '/'
            }}
            style={{
              fontSize: 14,
              minHeight: 36,
              padding: '8px 20px',
              borderRadius: 8,
              border: '1px solid var(--color-border, #333)',
              background: 'transparent',
              color: 'var(--mol-color-text-secondary, #aaaaaa)',
              cursor: 'pointer',
            }}
          >
            {t('errorBoundary.goHome', undefined, { defaultValue: 'Go home' })}
          </button>
        </div>

        {this.state.error && (
          <div style={{ marginTop: 16, maxWidth: 500, width: '100%', textAlign: 'center' }}>
            {/* mol-bespoke-button: same eager-graph ClassMap exception as above.
                A show/hide disclosure, hand-matched to the `link` variant
                DESIGN.md reserves for show/hide toggles. */}
            <button
              type="button"
              data-mol-id="error-boundary-details-toggle"
              aria-expanded={this.state.showDetail}
              onClick={() => this.setState((s) => ({ showDetail: !s.showDetail }))}
              style={{
                fontSize: 12,
                minHeight: 36,
                color: 'var(--mol-color-text-secondary, #888)',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                textDecoration: 'underline',
                padding: 0,
              }}
            >
              {this.state.showDetail
                ? t('errorBoundary.hideDetails', undefined, { defaultValue: 'Hide details' })
                : t('errorBoundary.showDetails', undefined, { defaultValue: 'Show details' })}
            </button>
            {this.state.showDetail && (
              <pre
                style={{
                  marginTop: 8,
                  padding: 12,
                  borderRadius: 8,
                  background: 'rgba(255,255,255,0.04)',
                  border: '1px solid rgba(255,255,255,0.06)',
                  fontSize: 12,
                  color: 'var(--mol-color-error, #ef4444)',
                  overflow: 'auto',
                  maxHeight: 200,
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                }}
              >
                {this.state.error.message}
                {this.state.error.stack && `\n\n${this.state.error.stack}`}
              </pre>
            )}
          </div>
        )}
      </div>
    )
  }
}
